<template>
  <Card class="m-2">
    <template #title=""
      >ยินดีต้อนรับคุณ {{ store.name ?? "N/A" }}
      <span class="text-sm">({{ store.username }})</span></template
    >
    <template #content="">
      <div class="grid">
        <div class="col-12 lg:col-6">
          <DataTable
            :value="filteredDashboards"
            class="p-datatable-sm"
            :showGridlines="true"
            scrollable
            scrollHeight="70vh"
            :loading="isLoading"
          >
            <template #header="">
              <span class="p-float-label">
                <InputText
                  class="p-inputtext-sm"
                  v-model="searchString"
                  id="searchString"
                ></InputText>
                <label for="searchString">ค้นหา</label>
              </span>
            </template>
            <template #empty="">ไม่พบข้อมูล</template>
            <Column field="dman" header="Dman No." sortable></Column>
            <Column field="dmanName" header="Name" sortable></Column>
            <Column field="plant" header="Office" sortable>
              <template #body="props">
                <div
                  class="office-box mr-2"
                  :style="{
                    backgroundColor: salesOfficesColor[props.data.plant],
                  }"
                ></div>
                {{ props.data.plant }}
              </template>
            </Column>
            <Column field="shipmentNo" header="Shipment No." sortable>
              <template #body="props">
                <span
                  class="underline text-blue-500 cursor-pointer"
                  @click="showMap(props.data.shipmentNo)"
                  >{{ parseInt(props.data.shipmentNo) }}</span
                >
              </template>
            </Column>
            <Column field="timeStamp" header="เวลาที่ update" sortable>
              <template #body="props">
                <span v-tooltip="`${dayjs(props.data.timeStamp).fromNow()}`">{{
                  formatDateTime(props.data.timeStamp)
                }}</span>
              </template>
            </Column>
            <template #footer="">
              จำนวนพนักงานทั้งหมด {{ dashboards.length }} ท่าน
            </template>
          </DataTable>
        </div>
        <div class="col-12 lg:col-6">
          <DashboardMap
            :dashboards="filteredDashboards"
            :salesOfficesColor="salesOfficesColor"
          />
        </div>
      </div>
    </template>
  </Card>

  <Sidebar v-model:visible="showPopup" modal position="full">
    <shipment-map v-if="selectedShipment" :shipmentNo="selectedShipment" />
  </Sidebar>
</template>

<script lang="ts">
import { usePlugin } from "@/plugins";
import { DashboardView, GetDashboardResponse } from "@/types";
import { computed, defineComponent, onMounted, ref } from "vue";
import DashboardMap from "@/components/DashboardMap.vue";
import { useSessionStorage } from "@vueuse/core";
import Fuse from "fuse.js";
import ShipmentMap from "@/components/ShipmentMap.vue";

export default defineComponent({
  name: "Home",
  components: { DashboardMap, ShipmentMap },
  setup() {
    const { store, axios, error, formatDateTime, rainbow, dayjs } = usePlugin();

    const searchString = useSessionStorage("Home-searchString", "");
    const isLoading = ref(false);
    const dashboards = ref<DashboardView[]>([]);
    const showPopup = ref(false);
    const selectedShipment = ref("");
    const loadDashboard = async () => {
      isLoading.value = true;
      try {
        const result = await axios.get<GetDashboardResponse>(
          "api/v1/admin/dashboard"
        );
        dashboards.value = result.data.dashboards;
      } catch (e) {
        error(e);
      } finally {
        isLoading.value = false;
      }
    };

    const salesOfficesColor = computed((): { [key: string]: string } => {
      const offices = [...new Set(dashboards.value.map((x) => x.plant))];
      let i = 0;
      const result: { [key: string]: string } = {};
      offices.map((office) => (result[office] = rainbow(offices.length, i++)));
      return result;
    });

    const filteredDashboards = computed(() => {
      if (!searchString.value) return dashboards.value;
      const fuse = new Fuse(dashboards.value, {
        keys: ["dman", "dmanName", "shipmentNo", "plant"],
        threshold: 0.2,
      });
      const result = fuse.search(searchString.value);
      return result.map((x) => x.item);
    });

    const showMap = (shipmentNo: string) => {
      showPopup.value = true;
      selectedShipment.value = shipmentNo;
    };

    onMounted(() => {
      loadDashboard();
    });

    return {
      store,
      dashboards,
      formatDateTime,
      rainbow,
      dayjs,
      isLoading,
      searchString,
      salesOfficesColor,
      filteredDashboards,
      showMap,
      showPopup,
      selectedShipment,
    };
  },
});
</script>

<style scoped>
.office-box {
  width: 0.6rem;
  height: 0.6rem;
  border: 1px black solid;
}
</style>
